// src/pages/BlogDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { fadeIn } from '../styles/animations';
import { blogService, categoriesService } from '../services/api';
import Button from '../components/ui/Button';

const BlogDetailsWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const BlogHero = styled.section`
  position: relative;
  height: 50vh;
  min-height: 400px;
  max-height: 600px;
  overflow: hidden;
  padding: 0 !important; 

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7));
    z-index: 1;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.light};
  z-index: 2;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const BlogCategory = styled.span`
  display: inline-block;
  padding: 0.3rem 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const BlogTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.xxxl};
  max-width: 800px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const BlogDate = styled.div`
  font-size: ${props => props.theme.fontSizes.medium};
  opacity: 0.9;
`;

const BlogContent = styled.section`
  background-color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 1s ease-out forwards;
  
  p, ul, ol {
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: 1.8;
  }
  
  h2 {
    font-size: ${props => props.theme.fontSizes.xl};
    margin: ${props => props.theme.spacing.lg} 0 ${props => props.theme.spacing.md};
  }
  
  h3 {
    font-size: ${props => props.theme.fontSizes.large};
    margin: ${props => props.theme.spacing.md} 0;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.medium};
    margin: ${props => props.theme.spacing.md} 0;
  }
  
  blockquote {
    border-left: 4px solid ${props => props.theme.colors.primary};
    padding: ${props => props.theme.spacing.md};
    margin: ${props => props.theme.spacing.md} 0;
    background-color: ${props => props.theme.colors.gray};
    font-style: italic;
  }
`;

const RelatedPosts = styled.section`
  background-color: ${props => props.theme.colors.gray};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const RelatedContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const RelatedTitle = styled.h2`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  &:after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
    margin: ${props => props.theme.spacing.sm} auto 0;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .related-image img {
      transform: scale(1.05);
    }
  }
`;

const RelatedImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const RelatedContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const RelatedPostTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: ${props => props.theme.spacing.sm};
  
  a {
    color: ${props => props.theme.colors.textMain};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.fast};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const RelatedDate = styled.p`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 600;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid #eee;
`;

const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  transition: color ${props => props.theme.transitions.fast};
  
  svg {
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
  
  &.prev:hover svg {
    transform: translateX(-5px);
  }
  
  &.next:hover svg {
    transform: translateX(5px);
  }
  
  &.prev svg {
    margin-right: 0.5rem;
  }
  
  &.next svg {
    margin-left: 0.5rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  h2 {
    margin-bottom: ${props => props.theme.spacing.md};
  }
  
  p {
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.textLight};
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textLight};
  }
`;

const BlogImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin: ${props => props.theme.spacing.md} 0;
`;

const BlogDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [category, setCategory] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [previousPost, setPreviousPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour formater les dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  useEffect(() => {
    const fetchPostDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Récupérer l'article
        const postData = await blogService.getBySlug(slug);
        if (!postData) {
          throw new Error("Article introuvable");
        }

        setPost(postData);

        // Récupérer la catégorie si elle existe
        if (postData.category_id && postData.category_slug) {
          try {
            const categoryData = await categoriesService.getBySlug(postData.category_slug);
            setCategory(categoryData);

            // Récupérer les articles liés (même catégorie)
            const relatedData = await blogService.getByCategory(postData.category_slug);

            // Filtrer pour exclure l'article actuel et limiter à 3
            let relatedArticles = [];
            if (relatedData && relatedData.posts) {
              relatedArticles = relatedData.posts.filter(p => p.id !== postData.id).slice(0, 3);
            } else if (Array.isArray(relatedData)) {
              relatedArticles = relatedData.filter(p => p.id !== postData.id).slice(0, 3);
            }
            setRelatedPosts(relatedArticles);
          } catch (categoryError) {
            console.error("Erreur lors du chargement de la catégorie:", categoryError);
            // Ne pas échouer complètement si seulement la catégorie pose problème
          }
        }

        // Pour la navigation précédent/suivant, on pourrait faire une demande supplémentaire
        // à l'API pour obtenir les articles adjacents. Pour cette implémentation,
        // on va simplement ne pas afficher ces liens plutôt que de simuler des données.
        setPreviousPost(null);
        setNextPost(null);

      } catch (err) {
        console.error("Erreur lors du chargement de l'article:", err);
        setError("Nous n'avons pas pu charger cet article. Veuillez réessayer ultérieurement.");
      } finally {
        setLoading(false);
      }
    };

    // Si le slug change, recharger les données
    fetchPostDetails();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <BlogDetailsWrapper>
        <LoadingIndicator>
          <p>Chargement de l'article...</p>
        </LoadingIndicator>
      </BlogDetailsWrapper>
    );
  }

  if (error || !post) {
    return (
      <BlogDetailsWrapper>
        <ErrorMessage>
          <h2>Article introuvable</h2>
          <p>{error || "Cet article n'existe pas ou a été supprimé."}</p>
          <Button variant="primary" to="/blog">
            Retour au blog
          </Button>
        </ErrorMessage>
      </BlogDetailsWrapper>
    );
  }

  // Préparer l'URL de l'image principale
  const featuredImageUrl = post.featured_image
    ? `https://decko.fr/uploads/${post.featured_image}`
    : 'https://via.placeholder.com/1200x600?text=Decko+Blog';

  // Formatter la date
  const formattedDate = formatDate(post.created_at);

  return (
    <BlogDetailsWrapper>
      <Helmet>
        <title>{post.title} - Decko | Blog</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>

      <BlogHero>
        <HeroImage src={featuredImageUrl} alt={post.title} />
        <HeroContent>
          {category && (
            <BlogCategory>{category.name}</BlogCategory>
          )}
          <BlogTitle>{post.title}</BlogTitle>
          <BlogDate>{formattedDate}</BlogDate>
        </HeroContent>
      </BlogHero>

      <BlogContent>
        <ContentContainer>
          {/* Contenu de l'article */}
          <div dangerouslySetInnerHTML={{ __html: post.content }} />

          {/* Affichage des images supplémentaires si disponibles */}
          {post.images && post.images.length > 0 && (
            <div>
              {post.images.map((image, index) => (
                <BlogImage
                  key={image.id || index}
                  src={`https://decko.fr/uploads/${image.filename}`}
                  alt={image.alt_text || `Image ${index + 1} de ${post.title}`}
                />
              ))}
            </div>
          )}

          {/* Navigation précédent/suivant */}
          <Navigation>
            {previousPost && (
              <NavLink to={`/blog/${previousPost.slug}`} className="prev">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
                </svg>
                Article précédent
              </NavLink>
            )}

            {/* Toujours afficher un lien pour retourner à la liste des articles */}
            {!previousPost && !nextPost && (
              <NavLink to="/blog" className="prev">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
                </svg>
                Retour au blog
              </NavLink>
            )}

            {nextPost && (
              <NavLink to={`/blog/${nextPost.slug}`} className="next">
                Article suivant
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                </svg>
              </NavLink>
            )}
          </Navigation>
        </ContentContainer>
      </BlogContent>

      {/* Articles similaires */}
      {relatedPosts.length > 0 && (
        <RelatedPosts>
          <RelatedContainer>
            <RelatedTitle>Articles similaires</RelatedTitle>
            <RelatedGrid>
              {relatedPosts.map(relatedPost => (
                <RelatedCard key={relatedPost.id}>
                  <RelatedImage className="related-image">
                    <img
                      src={relatedPost.featured_image
                        ? `https://decko.fr/uploads/${relatedPost.featured_image}`
                        : 'https://via.placeholder.com/400x200?text=Decko+Blog'}
                      alt={relatedPost.title}
                    />
                  </RelatedImage>
                  <RelatedContent>
                    <RelatedDate>{formatDate(relatedPost.created_at)}</RelatedDate>
                    <RelatedPostTitle>
                      <Link to={`/blog/${relatedPost.slug}`}>{relatedPost.title}</Link>
                    </RelatedPostTitle>
                    <p>{relatedPost.excerpt}</p>
                    <Button
                      variant="text"
                      to={`/blog/${relatedPost.slug}`}
                    >
                      Lire l'article
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                      </svg>
                    </Button>
                  </RelatedContent>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedContainer>
        </RelatedPosts>
      )}
    </BlogDetailsWrapper>
  );
};

export default BlogDetails;